// RecordingStudio params panel — middle column of the Recording Booth
// overlay. Three sliders (Stability / Similarity / Style Exaggeration) and
// two toggles (Speaker Boost / Remove Background Noise). State lives in the
// parent overlay — this is a pure presentational component.

import type { STSParams } from '@shared/types';
import styles from './params.module.css';

interface Props {
  params: STSParams;
  onChange(next: STSParams): void;
  /** When true, inputs render disabled (e.g. while generating). */
  disabled?: boolean;
}

export function RecordingStudioParams({ params, onChange, disabled }: Props) {
  return (
    <div className={styles.panel}>
      <div className={styles.header}>Parameters for STS</div>

      <div className={styles.sliders}>
        <SliderItem
          label="Stability"
          endpoints={['More variable', 'More stable']}
          value={params.stability}
          disabled={disabled}
          onChange={(v) => onChange({ ...params, stability: v })}
          warning={params.stability < 0.3 ? 'Under 30% may lead to instability' : null}
        />
        <SliderItem
          label="Similarity"
          endpoints={['Low', 'High']}
          value={params.similarity}
          disabled={disabled}
          onChange={(v) => onChange({ ...params, similarity: v })}
        />
        <SliderItem
          label="Style Exaggeration"
          endpoints={['None', 'Exaggerated']}
          value={params.style}
          disabled={disabled}
          onChange={(v) => onChange({ ...params, style: v })}
          warning={params.style > 0.5 ? 'Over 50% may lead to instability' : null}
        />
      </div>

      <div className={styles.toggles}>
        <ToggleRow
          label="Speaker Boost"
          checked={params.speakerBoost}
          disabled={disabled}
          onChange={(v) => onChange({ ...params, speakerBoost: v })}
        />
        <ToggleRow
          label="Remove Background Noise"
          checked={params.removeNoise}
          disabled={disabled}
          onChange={(v) => onChange({ ...params, removeNoise: v })}
        />
      </div>
    </div>
  );
}

interface SliderItemProps {
  label: string;
  endpoints: [string, string];
  value: number; // 0..1
  onChange(v: number): void;
  warning?: string | null;
  disabled?: boolean;
}

function SliderItem({
  label,
  endpoints,
  value,
  onChange,
  warning,
  disabled,
}: SliderItemProps) {
  const pct = Math.round(value * 100);
  // Decimal readout (0.50) per spec.
  const readout = value.toFixed(2);
  return (
    <div className={styles.sliderBlock}>
      <div className={styles.sliderHeader}>
        <span className={styles.sliderLbl}>{label}</span>
        <span className={styles.sliderVal}>{readout}</span>
      </div>
      <div className={styles.sliderEndpoints}>
        <span>{endpoints[0]}</span>
        <span>{endpoints[1]}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={pct}
        disabled={disabled}
        className={styles.range}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
      />
      {warning && <div className={styles.sliderWarning}>{warning}</div>}
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
  disabled?: boolean;
}

function ToggleRow({ label, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className={styles.toggleRow}>
      <span className={styles.toggleLbl}>{label}</span>
      <label
        className={`${styles.toggle} ${disabled ? styles.toggleDisabled : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className={styles.toggleSl} />
      </label>
    </div>
  );
}
